import { useShell } from "./app-shell";
import { LogoMark } from "./logo";
import { Skeleton } from "./primitives";
import { Spinner } from "./spinner";

/**
 * What a screen shows while it loads, in place of a blank page. Inside the
 * app's frame it is a placeholder card in the shape of a screen; before the
 * frame exists (the first load, or signing in) it is the logo and a spinner in
 * the middle of the window. Give it to a router as its pending component.
 *
 * It fades in a moment after it mounts, so a quick load shows nothing; the
 * router adds its own short wait before mounting it at all.
 */
export function RoutePending() {
  const { inShell } = useShell();
  return inShell ? <PagePending /> : <ScreenPending />;
}

/** A placeholder screen inside the frame: a heading, a line of detail, a few rows. */
function PagePending() {
  return (
    <div
      role="status"
      className="flex flex-col gap-y-3 animate-in fade-in-0 fill-mode-both delay-100 duration-300"
    >
      <span className="sr-only">Loading</span>
      <div className="w-full rounded-lg bg-ui-bg-base shadow-elevation-card-rest">
        <div className="flex items-center justify-between gap-x-4 px-6 py-4">
          <div className="flex min-w-0 flex-1 flex-col gap-y-2">
            <Skeleton className="h-5 w-48 max-w-full" />
            <Skeleton className="h-4 w-72 max-w-full" />
          </div>
          <Spinner className="shrink-0 text-ui-fg-muted" />
        </div>
        <div className="flex flex-col gap-y-3 border-t border-ui-border-base px-6 py-5">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      </div>
    </div>
  );
}

/** The whole window, before the frame: the mark and a spinner, centred. */
function ScreenPending() {
  return (
    <div
      role="status"
      className="flex min-h-dvh flex-col items-center justify-center gap-y-5 bg-ui-bg-subtle animate-in fade-in-0 fill-mode-both delay-100 duration-300"
    >
      <LogoMark className="size-8" />
      <Spinner className="text-ui-fg-muted" />
      <span className="sr-only">Loading LumberSet</span>
    </div>
  );
}
